import React, { useState } from 'react';

export interface CustomImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  webpSrc?: string;
  alt: string;
  width: number | string;
  height: number | string;
  className?: string;
  priority?: boolean;
  fallbackSrc?: string;
  sizes?: string;
}

/**
 * CustomImage component that automatically utilizes modern WebP picture sources,
 * responsive srcsets, explicit width/height dimensions, alternating text (alt),
 * Schema.org microdata (itemProp="image"), and high-resolution exportability.
 */
export const CustomImage: React.FC<CustomImageProps> = ({
  src,
  webpSrc,
  alt,
  width,
  height,
  className = '',
  priority = false,
  fallbackSrc = '/thinkers-arcade-logo.webp',
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 800px',
  title,
  ...rest
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  // Automatically derive modern .webp companion if available or not already webp
  const computedWebp =
    webpSrc ||
    (src.endsWith('.webp')
      ? src
      : src.replace(/\.(jpg|jpeg|png)$/i, '.webp'));

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      if (fallbackSrc) {
        setCurrentSrc(fallbackSrc);
      }
    }
  };

  // Convert width and height to style aspect-ratio if numeric to prevent layout shift (CLS)
  const numericWidth = typeof width === 'number' ? width : parseInt(width as string, 10);
  const numericHeight = typeof height === 'number' ? height : parseInt(height as string, 10);
  const aspectRatioStyle =
    !isNaN(numericWidth) && !isNaN(numericHeight) && numericHeight > 0
      ? { aspectRatio: `${numericWidth} / ${numericHeight}` }
      : undefined;

  // Meaningful fallback alternating text if alt is somehow omitted
  const cleanAlt = alt || 'Thinkers Arcade Enterprise Technology & Software Engineering Kenya';

  return (
    <picture className="contents">
      {computedWebp && !hasError && (
        <source
          type="image/webp"
          srcSet={`${computedWebp} 1x`}
          sizes={sizes}
        />
      )}
      <img
        src={currentSrc}
        alt={cleanAlt}
        title={title || cleanAlt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding={priority ? 'sync' : 'async'}
        sizes={sizes}
        itemProp="image"
        style={{ ...aspectRatioStyle, ...rest.style }}
        onError={handleError}
        className={`${className} transition-opacity duration-300 select-auto`}
        {...rest}
      />
    </picture>
  );
};
